import React from "react";

import type { AssociatedPitchedContent } from "../../../../store/editContent";
import type { KeyValue } from "../../../../types/common";
import {
    getPitchedContentDetails,
    PITCHED_CONTENTS_CARD_LABEL_ACTIVITY,
    PITCHED_CONTENTS_CARD_LABEL_NOTES,
    PITCHED_CONTENTS_CARD_LABEL_OPPORTUNITY,
    PITCHED_CONTENTS_CARD_LABEL_SALESFORCE_ACCOUNT,
    PITCHED_CONTENTS_CARD_LABEL_STATUS,
    PITCHED_CONTENTS_CARD_LABEL_TOTAL_OPPORTUNITY_VALUE,
} from "../../../../utils/pitchedContentDetails.utils";
import {
    ACTIVITY_NOT_CREATED_DROPDOWN_LABEL,
    OPPORTUNITY_NOT_CREATED_DROPDOWN_LABEL,
    OpportunityStatusText,
} from "../../../../utils/salesforce.utils";
import { formatAsCurrency } from "../../../../utils/string.utils";
import { CardLayoutV3 } from "../insightDetails/CardLayoutV3";

import { PitchedContentCardAction } from "./PitchedContentCardAction";

export interface PitchedContentCardProps {
    content: AssociatedPitchedContent;
    key: string;
    isEditable: boolean;
    isSharable: boolean;
}

export const PitchedContentCard: React.FunctionComponent<
    PitchedContentCardProps
> = ({ key, content, isEditable, isSharable }) => {
    const pitchedContentDetails = getPitchedContentDetails(content);

    const {
        contentId,
        contentTitle,
        contentType,
        associations,
        contentNote,
        lastModifiedDate,
    } = content;

    const association = associations[0];
    const salesforceAccountMetadata = association?.salesforceAccountMetadata;
    const salesforceActivityMetadata = association?.salesforceActivityMetadata;
    const salesforceOpportunityMetadata =
        association?.salesforceOpportunityMetadata;

    const additionalInfo: KeyValue = {
        [PITCHED_CONTENTS_CARD_LABEL_SALESFORCE_ACCOUNT]:
            salesforceAccountMetadata?.accountName,
        [PITCHED_CONTENTS_CARD_LABEL_ACTIVITY]:
            salesforceActivityMetadata?.activityName ??
            ACTIVITY_NOT_CREATED_DROPDOWN_LABEL,
        [PITCHED_CONTENTS_CARD_LABEL_OPPORTUNITY]:
            salesforceOpportunityMetadata?.opportunityName ??
            OPPORTUNITY_NOT_CREATED_DROPDOWN_LABEL,
        [PITCHED_CONTENTS_CARD_LABEL_STATUS]:
            salesforceOpportunityMetadata?.opportunityStatus
                ? OpportunityStatusText[
                      salesforceOpportunityMetadata.opportunityStatus
                  ]
                : "-",
        [PITCHED_CONTENTS_CARD_LABEL_TOTAL_OPPORTUNITY_VALUE]: formatAsCurrency(
            salesforceOpportunityMetadata?.opportunityAmount,
            salesforceOpportunityMetadata?.opportunityCurrency,
        ),
        [PITCHED_CONTENTS_CARD_LABEL_NOTES]: contentNote,
    };

    return (
        <CardLayoutV3
            cardHeader={contentTitle ?? ""}
            insightType={contentType}
            insightTypeName={contentType}
            additionalInfo={additionalInfo as KeyValue}
            lastModifiedDate={lastModifiedDate}
            actions={
                <PitchedContentCardAction
                    content={content}
                    details={pitchedContentDetails}
                    isEditable={isEditable}
                    isSharable={isSharable}
                />
            }
            dataTestId={contentId ?? ""}
            key={key}
        />
    );
};
